
import React, { useState } from 'react';
import { supabase } from '../services/supabaseClient';
import { UserRole } from '../types';
import { useNavigate, Link } from 'react-router-dom';
import { ChurchIcon, MapPin, Globe, Mail, Lock, Layout, AlertTriangle, CheckCircle } from 'lucide-react';
import { checkContentSafety } from '../services/geminiService';

const ChurchRegistration: React.FC = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isSuccess, setIsSuccess] = useState(false);

    // Auth Data
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [repName, setRepName] = useState('');

    // Church Data
    const [churchName, setChurchName] = useState('');
    const [country, setCountry] = useState('');
    const [region, setRegion] = useState('');
    const [address, setAddress] = useState('');
    const [website, setWebsite] = useState('');
    const [description, setDescription] = useState('');

    const handleRegistration = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            // 1. Safety Check
            const safetyCheck = await checkContentSafety(`${churchName} ${description}`);
            if (!safetyCheck.safe) {
                throw new Error(`Content flagged: ${safetyCheck.reason}`);
            }

            // 2. Create Auth User & Pass Church Data in Metadata
            // The database trigger 'handle_new_user' will read this metadata
            // and create both the Church record and the Profile record atomically.
            const { data, error: authError } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    emailRedirectTo: window.location.origin,
                    data: { 
                        name: repName,
                        role: UserRole.CHURCH_REP,
                        voluntary_call: 'Church Administrator',
                        // Church Data for Trigger
                        church_name: churchName,
                        country,
                        region,
                        address,
                        website,
                        description
                    }
                }
            });

            if (authError) throw authError;

            // If successful, show the success screen
            setIsSuccess(true);

        } catch (err: any) {
            console.error(err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    if (isSuccess) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full bg-white p-10 rounded-xl shadow-lg border border-gray-100 text-center animate-fadeIn">
                    <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                        <CheckCircle className="h-8 w-8 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Registration Successful!</h2>
                    <p className="text-gray-600 mb-6">
                        We have sent a confirmation email to <span className="font-bold text-gray-800">{email}</span>.
                    </p>
                    <div className="bg-blue-50 text-blue-800 p-4 rounded-lg text-sm mb-6 border border-blue-100">
                        Please check your inbox (and spam folder) and click the confirmation link to activate your church account and log in.
                    </div>
                    <Link to="/" className="block w-full bg-blue-600 text-white py-2 rounded-lg font-bold hover:bg-blue-700 transition">
                        Return to Login
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-xl shadow-lg border border-gray-100">
                <div className="text-center">
                    <div className="mx-auto h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <ChurchIcon className="h-6 w-6 text-blue-600" />
                    </div>
                    <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                        Register your Ministry
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                        Create a dedicated profile for your church to manage events, pastors, and connect with members.
                    </p>
                </div>

                {error && (
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <AlertTriangle className="h-5 w-5 text-red-500" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                <form className="mt-8 space-y-6" onSubmit={handleRegistration}>
                    {step === 1 && (
                        <div className="space-y-4 animate-fadeIn">
                            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Account Details</h3>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Representative Name</label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Layout className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        required
                                        className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md py-2 border"
                                        placeholder="John Doe"
                                        value={repName}
                                        onChange={e => setRepName(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Official Email</label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Mail className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="email"
                                        required
                                        className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md py-2 border"
                                        placeholder="admin@church.org"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Password</label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Lock className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="password"
                                        required
                                        className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md py-2 border"
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Link to="/" className="w-1/3 flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                                    Cancel
                                </Link>
                                <button
                                    type="button"
                                    onClick={() => setStep(2)}
                                    disabled={!email || !password || !repName}
                                    className="w-2/3 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                >
                                    Next: Church Info
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-4 animate-fadeIn">
                             <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Church Information</h3>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Church Name</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors"
                                    placeholder="Grace Community Church"
                                    value={churchName}
                                    onChange={e => setChurchName(e.target.value)}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Country</label>
                                    <input
                                        type="text"
                                        required
                                        className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors"
                                        placeholder="USA"
                                        value={country}
                                        onChange={e => setCountry(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Region/State</label>
                                    <input
                                        type="text"
                                        required
                                        className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors"
                                        placeholder="California"
                                        value={region}
                                        onChange={e => setRegion(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Address</label>
                                <div className="relative">
                                    <MapPin className="h-4 w-4 text-gray-400 absolute left-0 top-3" />
                                    <input
                                        type="text"
                                        required
                                        className="w-full bg-transparent border-b border-gray-300 rounded-none pl-6 pr-2 py-2 focus:outline-none focus:border-blue-600 transition-colors"
                                        placeholder="123 Faith St"
                                        value={address}
                                        onChange={e => setAddress(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Website (Optional)</label>
                                <div className="relative">
                                    <Globe className="h-4 w-4 text-gray-400 absolute left-0 top-3" />
                                    <input
                                        type="url"
                                        className="w-full bg-transparent border-b border-gray-300 rounded-none pl-6 pr-2 py-2 focus:outline-none focus:border-blue-600 transition-colors"
                                        placeholder="https://grace.church"
                                        value={website}
                                        onChange={e => setWebsite(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Description</label>
                                <textarea
                                    className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors resize-none h-20"
                                    placeholder="Tell us about your mission..."
                                    value={description}
                                    onChange={e => setDescription(e.target.value)}
                                />
                            </div>

                            <div className="flex gap-4">
                                <button
                                    type="button"
                                    onClick={() => setStep(1)}
                                    className="w-1/3 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                                >
                                    Back
                                </button>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-2/3 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                >
                                    {isLoading ? 'Processing...' : 'Register Church'}
                                </button>
                            </div>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default ChurchRegistration;
